import { useTranslation } from "react-i18next";

import type { AdminPracticeQuestionInput } from "@/lib/admin-api";

/**
 * apps/web :: components/admin :: QuestionPreviewCard
 *
 * Admin-only preview of a practice question. Mirrors the presentational
 * rhythm of `components/practice/QuestionCard` (category badge +
 * difficulty indicator + question heading) but ALSO shows the correct
 * answer + explanation inline — this is deliberately the admin view,
 * never shipped to learners. Feeds off the live editor-form state so
 * content ops can see the learner-facing render without navigating
 * away from the form.
 */
export interface QuestionPreviewCardProps {
  question: AdminPracticeQuestionInput;
}

export default function QuestionPreviewCard({ question }: QuestionPreviewCardProps) {
  const { t } = useTranslation();
  const categoryKey = question.category.toLowerCase();
  const categoryLabel = t(`admin.category.${categoryKey}`, {
    defaultValue: categoryKey.replaceAll("_", " "),
  });

  return (
    <section
      data-testid="admin-preview-card"
      aria-labelledby="admin-preview-heading"
      className="rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
        {t("admin.editor.preview.heading")}
      </p>
      <div className="mt-sm flex items-center justify-between gap-sm text-xs">
        <span className="rounded-full bg-brand-primary/10 px-sm py-xs font-semibold uppercase tracking-wide text-brand-primary">
          {categoryLabel}
        </span>
        <span
          aria-label={t("admin.editor.fields.difficultyValue", {
            n: question.difficulty,
          })}
          className="text-neutral-600"
        >
          {t("admin.editor.fields.difficultyValue", { n: question.difficulty })}
        </span>
      </div>
      <h2
        id="admin-preview-heading"
        data-testid="admin-preview-question-text"
        className="mt-md font-display text-xl font-bold leading-snug text-brand-primary sm:text-2xl"
      >
        {question.question_text || "—"}
      </h2>
      <ul
        data-testid="admin-preview-choices"
        aria-label={t("admin.editor.fields.choices")}
        className="mt-md space-y-xs"
      >
        {question.choices.map((c) => {
          const isCorrect = c.id === question.correct_choice_id;
          return (
            <li
              key={c.id || Math.random()}
              data-testid={`admin-preview-choice-${c.id}`}
              data-correct={isCorrect ? "true" : "false"}
              className={`flex items-start gap-sm rounded-md border px-sm py-xs text-sm ${
                isCorrect
                  ? "border-success/60 bg-success/5 text-success"
                  : "border-neutral-200 bg-white text-neutral-800"
              }`}
            >
              <span className="font-mono text-xs">{c.id.toUpperCase()}.</span>
              <span className="flex-1">{c.text || "—"}</span>
              {isCorrect && (
                <span className="text-xs font-semibold uppercase tracking-wide">
                  {t("admin.editor.preview.correctBadge")}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <div className="mt-md rounded-md border border-neutral-200 bg-neutral-50 p-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
          {t("admin.editor.preview.explanationHeading")}
        </p>
        <p
          data-testid="admin-preview-explanation"
          className="mt-xs text-sm text-neutral-800"
        >
          {question.explanation || "—"}
        </p>
        {question.handbook_section && (
          <p className="mt-xs text-xs text-neutral-600">
            {question.handbook_section}
          </p>
        )}
      </div>
    </section>
  );
}
